/**
 * Option chain table for the covered-call wizard.
 *
 * Lists call contracts for a given expiration with bid/ask/mid/last,
 * pre-highlights the strike that's closest to ≥10% above the current
 * underlying price (the strategy's default), and lets the user click a
 * row to select.
 */

import React, { useMemo } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

export interface OptionContract {
  symbol: string;
  underlying: string;
  strike: number;
  expiration: string;
  type: 'call' | 'put';
  bid: number | null;
  ask: number | null;
  last: number | null;
}

interface Props {
  contracts: OptionContract[];
  loading?: boolean;
  currentPrice: number | null;
  selectedStrike: number | null;
  onSelect: (contract: OptionContract) => void;
}

const fmt = (n: number | null | undefined): string =>
  n === null || n === undefined || Number.isNaN(n)
    ? '—'
    : n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const mid = (bid: number | null, ask: number | null): number | null =>
  bid && ask && bid > 0 && ask > 0 ? (bid + ask) / 2 : null;

const OptionChainTable: React.FC<Props> = ({
  contracts,
  loading,
  currentPrice,
  selectedStrike,
  onSelect,
}) => {
  // Pre-compute the "recommended" strike: the smallest strike ≥ currentPrice * 1.10.
  // This matches the strategy's default selection rule on auto-rolls.
  const recommendedStrike = useMemo<number | null>(() => {
    if (!currentPrice || !contracts.length) return null;
    const target = currentPrice * 1.1;
    const sorted = [...contracts].sort((a, b) => a.strike - b.strike);
    const above = sorted.find((c) => c.strike >= target);
    return above?.strike ?? sorted[sorted.length - 1].strike;
  }, [contracts, currentPrice]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!contracts.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        No call contracts available for this expiration.
      </Typography>
    );
  }

  const sorted = [...contracts].sort((a, b) => a.strike - b.strike);

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Strike</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">Bid</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">Ask</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">Mid</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">Last</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">Premium / Contract</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="center">Tag</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((c) => {
            const m = mid(c.bid, c.ask);
            const pricePerShare = m ?? c.last ?? 0;
            const perContract = pricePerShare * 100;
            const isSelected = selectedStrike !== null && c.strike === selectedStrike;
            const isRecommended = recommendedStrike !== null && c.strike === recommendedStrike;
            const itm = currentPrice !== null && c.strike < currentPrice;
            return (
              <TableRow
                key={c.symbol}
                hover
                selected={isSelected}
                onClick={() => onSelect(c)}
                sx={{
                  cursor: 'pointer',
                  bgcolor: isSelected
                    ? 'action.selected'
                    : isRecommended
                    ? 'rgba(53,196,141,.14)'
                    : itm
                    ? 'action.hover'
                    : undefined,
                }}
              >
                <TableCell sx={{ fontWeight: 600 }}>${fmt(c.strike)}</TableCell>
                <TableCell align="right">${fmt(c.bid)}</TableCell>
                <TableCell align="right">${fmt(c.ask)}</TableCell>
                <TableCell align="right">${fmt(m)}</TableCell>
                <TableCell align="right">${fmt(c.last)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 500 }}>
                  ${fmt(perContract)}
                </TableCell>
                <TableCell align="center">
                  {isSelected && (
                    <Chip label="SELECTED" size="small" color="primary" />
                  )}
                  {!isSelected && isRecommended && (
                    <Chip label="≥10%" size="small" color="success" variant="outlined" />
                  )}
                  {!isSelected && !isRecommended && itm && (
                    <Chip label="ITM" size="small" color="warning" variant="outlined" />
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default OptionChainTable;
